import type React from 'react';
import { useEffect, useState, useTransition } from 'react';

import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';

import { findRouteValue } from '@shared/functions/findRouteValue';
import { getRoutePath } from '@shared/utils/hubs/get-route-path';

import type { HubTabsType } from '@shared/models/hubs/tabs.model';

/**
 * Props for the useTabs hook.
 *
 * @typedef {Object} HubTabsType
 * @property {TabConfig[]} configTabs - An array of tabs configurations for routing.
 * @property {number} defaultTabNameIndex - The default index of the tab to be displayed when no route matches.
 * @property {Record<string, number>} configTabsEnum - An enum consisting of a key value pair and the key is routing, the value is index
 * @property {boolean} isIncludesPathPattern - Indicates whether the routing path includes a pattern (control of links from outside, let’s say a user switches from a modal to another tab).
 * @property {boolean} isRecheckByLocation - Indicates whether to recheck the tab when the location changes.
 * @template T - Type of tab values.
 */

/**
 * Represents a custom React hook for managing tabs and navigation.
 *
 * @param {HubTabsType<T>} props - Props for the useTabs hook.
 * @property {number} tabsIndex - The index of the currently active tab.
 * @property {(event: React.SyntheticEvent, newValue: number) => void} handleChangeTab - Function to handle tab change events.
 */
export const useTabs = <T>({
  configTabs,
  defaultTabNameIndex,
  configTabsEnum,
  isIncludesPathPattern,
  isRecheckByLocation,
  isAddSearchParams,
}: HubTabsType<T>) => {
  const location = useLocation();
  const navigate = useNavigate();

  const routeTabName = getRoutePath(
    isAddSearchParams ? `${location.pathname + location.search}` : location.pathname,
  );
  const currentTabNameIndex = isIncludesPathPattern
    ? (findRouteValue<T>(location.pathname.replace(/\d+/g, ''), configTabsEnum) as number)
    : routeTabName
      ? // @ts-ignore
        (configTabsEnum[routeTabName] as number)
      : defaultTabNameIndex;

  const [, startTransition] = useTransition();
  const [tabsIndex, setTabsIndex] = useState(configTabs[currentTabNameIndex]?.index || defaultTabNameIndex);

  useEffect(() => {
    if (isRecheckByLocation) {
      const newRouterTabName = getRoutePath(
        isAddSearchParams ? `${location.pathname + location.search}` : location.pathname,
      );

      const newCurrentTabNameIndex = isIncludesPathPattern
        ? (findRouteValue<T>(location.pathname.replace(/\d+/g, ''), configTabsEnum) as number)
        : newRouterTabName
          ? // @ts-ignore
            (configTabsEnum[newRouterTabName] as number)
          : defaultTabNameIndex;
      setTabsIndex(configTabs[newCurrentTabNameIndex]?.index || defaultTabNameIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecheckByLocation, isIncludesPathPattern, location.pathname]);

  const configTabsFor = (aTabValue: number) => {
    return configTabs[aTabValue];
  };
  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = configTabsFor(newValue);

    if (selectedTab) {
      startTransition(() => {
        setTabsIndex(selectedTab.index);
        navigate(selectedTab.path);
      });
    } else {
      startTransition(() => {
        setTabsIndex(configTabs[defaultTabNameIndex].index);
        navigate(configTabs[defaultTabNameIndex].path);
      });
    }
  };

  return { tabsIndex, handleChangeTab };
};
